/**
 * Average Summary Block Component
 * Displays a single average score for a category in Overview
 */

import { ScoreGridBlock as ScoreGridBlockType } from "@/types/interview-feedback.types";
import { Button } from "@/components/ui/button";
import { ChevronRight, HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { logger } from "@/utils/logger"

interface AverageSummaryBlockProps {
  block: ScoreGridBlockType;
  onViewDetails?: () => void;
}

export const AverageSummaryBlock = ({
  block,
  onViewDetails,
}: AverageSummaryBlockProps) => {
  const { title, items, helper } = block;

  // Calculate average of all items
  // Backend always sends scores in the correct scale (0-maxScore)
  const totalScore = items.reduce((sum, item) => {
    const score = Math.min(Math.max(item.score, 0), item.maxScore);
    return sum + score;
  }, 0);
  logger.log("Block", block);
  const averageScore = totalScore / items.length;
  const maxScore = items[0]?.maxScore || 10;
  const percentage = Math.round((averageScore / maxScore) * 100);
  const barColor = "#3b82f6";
  const bgColor = "#1e293b";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-2">
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
        {helper && (
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <HelpCircle className="w-4 h-4" />
                  <span>What does this mean?</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="max-w-xs">
                <p className="text-sm whitespace-normal">{helper}</p>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {/* Average Score Display */}
      <div className="space-y-3 p-4 rounded-lg bg-muted/20 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-muted-foreground">
            Average
          </span>
          <div className="flex items-baseline gap-1">
            <span
              className="text-2xl font-bold tabular-nums"
              style={{ color: barColor }}
            >
              {averageScore.toFixed(1)}
            </span>
            <span className="text-sm text-muted-foreground">/ {maxScore}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div
          className="h-2 rounded-full overflow-hidden w-full"
          style={{ backgroundColor: bgColor }}
        >
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${percentage}%`,
              backgroundColor: barColor,
              boxShadow: `0 0 8px ${barColor}40`,
            }}
          />
        </div>
      </div>
    </div>
  );
};
